import { api } from "@/app/services/api.service";

export const INTEGRATIONS_HEALTH_PATH = "integration/api/v1/integrations/health";
export const INTEGRATIONS_AUDITS_PATH = "integration/api/v1/integrations/audits";

export const INTEGRATION_SERVICE_NAMES = [
  "URA",
  "NIRA",
  "UGPASS",
  "EPOSTA",
  "SIMPO",
  "NTR_MANAGER",
  "NIR",
  "EDMS",
  "BRS",
  "OSTICKET",
  "HELPDESK",
] as const;

export type IntegrationServiceName = (typeof INTEGRATION_SERVICE_NAMES)[number] | string;

export type HealthStatus = "UP" | "DOWN" | "DEGRADED" | "UNKNOWN" | string;

export type ServiceStatusEvent = {
  status: HealthStatus;
  previousStatus: HealthStatus;
  occurredAt: string | null;
  reason: string | null;
};

export type ServiceHealth = {
  serviceName: string;
  status: HealthStatus;
  consecutiveFailures: number;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastError: string | null;
  lastLatencyMs: number | null;
  lastCallerIp: string | null;
  lastCheckedAt: string | null;
  statusHistory: ServiceStatusEvent[];
};

export type IntegrationAuditRecord = {
  id: string;
  serviceName: string | null;
  operation: string | null;
  callerIp: string | null;
  outgoingUrl: string | null;
  httpMethod: string | null;
  requestHeaders: Record<string, unknown> | null;
  requestPayload: string | null;
  responseStatus: number | null;
  responsePayload: string | null;
  executionTimeMs: number | null;
  status: string | null;
  errorMessage: string | null;
  timestamp: number | null;
  formattedTimestamp: string | null;
  raw: Record<string, unknown>;
};

export type AuditPage = {
  content: IntegrationAuditRecord[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
};

export function extractErrorMessage(error: unknown, fallback: string) {
  const err = error as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };
  return (
    err.response?.data?.message ||
    err.response?.data?.error ||
    err.message ||
    fallback
  );
}

function unwrapPayload(res: { data: unknown }) {
  const data = res.data as Record<string, unknown>;
  if (
    data != null &&
    typeof data === "object" &&
    Object.prototype.hasOwnProperty.call(data, "data")
  ) {
    return data.data;
  }
  return data;
}

function readString(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (value != null && value !== "") return String(value);
  }
  return null;
}

function readNumber(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && !Number.isNaN(value)) return value;
    if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return null;
}

function parseStatusEvent(body: unknown): ServiceStatusEvent {
  if (body == null || typeof body !== "object") {
    return { status: "UNKNOWN", previousStatus: "UNKNOWN", occurredAt: null, reason: null };
  }
  const record = body as Record<string, unknown>;
  return {
    status: readString(record, ["status"]) || "UNKNOWN",
    previousStatus: readString(record, ["previousStatus", "previous_status"]) || "UNKNOWN",
    occurredAt: readString(record, ["occurredAt", "occurred_at"]),
    reason: readString(record, ["reason"]),
  };
}

export function parseServiceHealth(body: unknown): ServiceHealth {
  if (body == null || typeof body !== "object") {
    return {
      serviceName: "",
      status: "UNKNOWN",
      consecutiveFailures: 0,
      lastSuccessAt: null,
      lastFailureAt: null,
      lastError: null,
      lastLatencyMs: null,
      lastCallerIp: null,
      lastCheckedAt: null,
      statusHistory: [],
    };
  }

  const record = body as Record<string, unknown>;
  const historyRaw = record.statusHistory ?? record.status_history;
  const statusHistory = Array.isArray(historyRaw)
    ? historyRaw.map(parseStatusEvent)
    : [];

  return {
    serviceName: readString(record, ["serviceName", "service_name"]) || "",
    status: readString(record, ["status"]) || "UNKNOWN",
    consecutiveFailures: readNumber(record, ["consecutiveFailures", "consecutive_failures"]) || 0,
    lastSuccessAt: readString(record, ["lastSuccessAt", "last_success_at"]),
    lastFailureAt: readString(record, ["lastFailureAt", "last_failure_at"]),
    lastError: readString(record, ["lastError", "last_error"]),
    lastLatencyMs: readNumber(record, ["lastLatencyMs", "last_latency_ms"]),
    lastCallerIp: readString(record, ["lastCallerIp", "last_caller_ip"]),
    lastCheckedAt: readString(record, ["lastCheckedAt", "last_checked_at"]),
    statusHistory,
  };
}

export function parseHealthMatrix(body: unknown): ServiceHealth[] {
  if (body == null || typeof body !== "object") return [];
  if (Array.isArray(body)) return body.map(parseServiceHealth);

  return Object.entries(body as Record<string, unknown>).map(([key, value]) => {
    const parsed = parseServiceHealth(value);
    if (!parsed.serviceName) parsed.serviceName = key;
    return parsed;
  });
}

export function parseAuditRecord(body: unknown): IntegrationAuditRecord {
  if (body == null || typeof body !== "object") {
    return {
      id: "",
      serviceName: null,
      operation: null,
      callerIp: null,
      outgoingUrl: null,
      httpMethod: null,
      requestHeaders: null,
      requestPayload: null,
      responseStatus: null,
      responsePayload: null,
      executionTimeMs: null,
      status: null,
      errorMessage: null,
      timestamp: null,
      formattedTimestamp: null,
      raw: {},
    };
  }

  const record = body as Record<string, unknown>;
  const headers = record.requestHeaders ?? record.request_headers;
  let requestHeaders: Record<string, unknown> | null = null;
  if (headers != null && typeof headers === "object" && !Array.isArray(headers)) {
    requestHeaders = headers as Record<string, unknown>;
  }

  const requestPayload = record.requestPayload ?? record.request_payload;
  const responsePayload = record.responsePayload ?? record.response_payload;

  return {
    id: readString(record, ["id"]) || "",
    serviceName: readString(record, ["serviceName", "service_name"]),
    operation: readString(record, ["operation"]),
    callerIp: readString(record, ["callerIp", "caller_ip"]),
    outgoingUrl: readString(record, ["outgoingUrl", "outgoing_url"]),
    httpMethod: readString(record, ["httpMethod", "http_method"]),
    requestHeaders,
    requestPayload:
      requestPayload == null
        ? null
        : typeof requestPayload === "string"
          ? requestPayload
          : JSON.stringify(requestPayload),
    responseStatus: readNumber(record, ["responseStatus", "response_status"]),
    responsePayload:
      responsePayload == null
        ? null
        : typeof responsePayload === "string"
          ? responsePayload
          : JSON.stringify(responsePayload),
    executionTimeMs: readNumber(record, ["executionTimeMs", "execution_time_ms"]),
    status: readString(record, ["status"]),
    errorMessage: readString(record, ["errorMessage", "error_message"]),
    timestamp: readNumber(record, ["timestamp"]),
    formattedTimestamp: readString(record, ["formattedTimestamp", "formatted_timestamp"]),
    raw: record,
  };
}

function parseAuditPage(body: unknown, page: number, size: number): AuditPage {
  if (Array.isArray(body)) {
    const content = body.map(parseAuditRecord);
    return {
      content,
      totalElements: content.length,
      totalPages: content.length === 0 ? 0 : 1,
      page,
      size,
    };
  }

  if (body != null && typeof body === "object") {
    const obj = body as Record<string, unknown>;
    if (Array.isArray(obj.content)) {
      const content = obj.content.map(parseAuditRecord);
      const totalElements = readNumber(obj, ["totalElements", "total_elements"]) ?? content.length;
      const totalPages =
        readNumber(obj, ["totalPages", "total_pages"]) ??
        (totalElements === 0 ? 0 : Math.ceil(totalElements / size));
      return { content, totalElements, totalPages, page, size };
    }
    if (Array.isArray(obj.data)) {
      return parseAuditPage(obj.data, page, size);
    }
  }

  return { content: [], totalElements: 0, totalPages: 0, page, size };
}

export async function fetchAllServicesHealth(): Promise<ServiceHealth[]> {
  const res = await api().get(INTEGRATIONS_HEALTH_PATH);
  return parseHealthMatrix(unwrapPayload(res));
}

export async function fetchServiceHealth(serviceName: string): Promise<ServiceHealth> {
  const res = await api().get(`${INTEGRATIONS_HEALTH_PATH}/${encodeURIComponent(serviceName)}`);
  return parseServiceHealth(unwrapPayload(res));
}

export async function fetchIntegrationAudits(params: {
  serviceName?: string;
  page?: number;
  size?: number;
}): Promise<AuditPage> {
  const page = params.page ?? 0;
  const size = params.size ?? 20;
  const query: Record<string, string | number> = { page, size };
  if (params.serviceName) query.serviceName = params.serviceName;

  const res = await api().get(INTEGRATIONS_AUDITS_PATH, { params: query });
  return parseAuditPage(unwrapPayload(res), page, size);
}

export async function fetchIntegrationAuditById(id: string): Promise<IntegrationAuditRecord> {
  const res = await api().get(`${INTEGRATIONS_AUDITS_PATH}/${encodeURIComponent(id)}`);
  return parseAuditRecord(unwrapPayload(res));
}

export function healthStatusBadgeClass(status: string | null | undefined) {
  const value = (status || "").toUpperCase();
  if (value === "UP") return "bg-green-100 text-green-800";
  if (value === "DEGRADED") return "bg-amber-100 text-amber-800";
  if (value === "DOWN") return "bg-red-100 text-red-800";
  return "bg-gray-100 text-gray-600";
}

export function auditStatusBadgeClass(status: string | null | undefined) {
  const value = (status || "").toUpperCase();
  if (value === "SUCCESS" || value === "OK") return "bg-green-100 text-green-800";
  if (value === "TIMEOUT" || value === "FAILED" || value === "ERROR") {
    return "bg-red-100 text-red-800";
  }
  return "bg-gray-100 text-gray-600";
}
